import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Timer, Plus, Minus, Trophy, Save, Loader2, Clock, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = ['1st', '2nd', '3rd', '4th', 'OT', 'Final'];

export default function LiveScoringPanel({ orgId, teamId }) {
  const { isDark } = useTheme();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [period, setPeriod] = useState(0);
  const [periodLabel, setPeriodLabel] = useState('1st');
  const [status, setStatus] = useState('not_started');
  const [saving, setSaving] = useState(false);
  const [scoringLog, setScoringLog] = useState([]);

  const fetchGames = useCallback(async () => {
    if (!orgId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/game-schedule/${orgId}`);
      const upcoming = (data || []).filter(g => {
        const d = new Date(g.date);
        const now = new Date();
        const diff = (d - now) / (1000 * 60 * 60 * 24);
        return diff >= -1 && diff <= 14;
      }).sort((a, b) => new Date(a.date) - new Date(b.date));
      setGames(upcoming);
    } catch (err) {
      console.error('Failed to fetch games:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchGames(); }, [fetchGames]);

  const selectGame = async (game) => {
    setSelectedGame(game);
    try {
      const { data } = await axios.get(`${API_URL}/api/stats/games/${game.id}/live-score`);
      setHomeScore(data.home_score || 0);
      setAwayScore(data.away_score || 0);
      setPeriod(data.period || 0);
      setPeriodLabel(data.period_label || '1st');
      setStatus(data.status || 'not_started');
      setScoringLog(data.scoring_log || []);
    } catch {
      setHomeScore(0); setAwayScore(0); setPeriod(0); setStatus('not_started'); setScoringLog([]);
    }
  };

  const adjustScore = (team, delta) => {
    const now = new Date().toISOString();
    if (team === 'home') {
      const ns = Math.max(0, homeScore + delta);
      setHomeScore(ns);
      if (delta > 0) setScoringLog([...scoringLog, { team: 'home', points: delta, period: periodLabel, time: now }]);
    } else {
      const ns = Math.max(0, awayScore + delta);
      setAwayScore(ns);
      if (delta > 0) setScoringLog([...scoringLog, { team: 'away', points: delta, period: periodLabel, time: now }]);
    }
  };

  const saveScore = async (finalStatus) => {
    if (!selectedGame) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/stats/games/${selectedGame.id}/live-score`, {
        home_score: homeScore,
        away_score: awayScore,
        period: period,
        period_label: periodLabel,
        status: finalStatus || status,
        scoring_log: scoringLog,
        org_id: orgId,
      });
      if (finalStatus === 'final') setStatus('final');
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const advancePeriod = () => {
    const idx = PERIODS.indexOf(periodLabel);
    if (idx < PERIODS.length - 1) {
      setPeriod(period + 1);
      setPeriodLabel(PERIODS[idx + 1]);
      if (PERIODS[idx + 1] === 'Final') {
        setStatus('final');
      } else {
        setStatus('in_progress');
      }
    }
  };

  const startGame = () => {
    setPeriod(1);
    setPeriodLabel('1st');
    setStatus('in_progress');
    setHomeScore(0);
    setAwayScore(0);
    setScoringLog([]);
  };

  const bg = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const txt = isDark ? 'text-white' : 'text-slate-900';
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;

  if (!selectedGame) {
    return (
      <div className="space-y-4" data-testid="live-scoring-panel">
        <div>
          <h3 className={`text-lg font-semibold ${txt}`}>Live Scoring</h3>
          <p className={`text-sm ${sub}`}>Select a game to start live scoring.</p>
        </div>
        {games.length === 0 ? (
          <Card className={bg}>
            <CardContent className="p-8 text-center">
              <Timer className={`h-12 w-12 mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
              <p className={sub}>No upcoming games found for this week.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-2">
            {games.map(g => (
              <Card key={g.id} className={`${bg} cursor-pointer hover:ring-1 hover:ring-amber-500/50 transition-all`} onClick={() => selectGame(g)} data-testid={`game-${g.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${txt}`}>{g.opponent} ({g.home_away?.toUpperCase()})</p>
                    <p className={`text-xs ${sub}`}>{new Date(g.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · {g.location || 'TBD'}</p>
                  </div>
                  <ChevronRight className={`h-5 w-5 ${sub}`} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="live-scoring-active">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setSelectedGame(null)} className={isDark ? 'text-slate-300' : ''} data-testid="back-to-games">
          Back to Games
        </Button>
        <Badge className={
          status === 'final' ? 'bg-slate-600' :
          status === 'in_progress' ? 'bg-green-600 animate-pulse' :
          status === 'halftime' ? 'bg-amber-600' : 'bg-slate-500'
        } data-testid="game-status-badge">
          {status === 'final' ? 'FINAL' : status === 'in_progress' ? `LIVE - ${periodLabel}` : status === 'halftime' ? 'HALFTIME' : 'NOT STARTED'}
        </Badge>
      </div>

      <Card className={bg}>
        <CardContent className="p-6">
          <div className="text-center mb-4">
            <p className={`text-xs uppercase tracking-wider ${sub}`}>{new Date(selectedGame.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
          </div>

          <div className="grid grid-cols-3 gap-4 items-center">
            {/* Home */}
            <div className="text-center">
              <p className={`text-sm font-medium mb-2 ${txt}`}>{selectedGame.home_away === 'home' ? 'HOME' : 'AWAY'}</p>
              <p className={`text-5xl font-bold tabular-nums ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} data-testid="home-score">{homeScore}</p>
              <p className={`text-xs mt-1 ${sub}`}>Our Team</p>
              {status === 'in_progress' && (
                <div className="flex justify-center gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => adjustScore('home', -1)} className={isDark ? 'border-slate-600' : ''} data-testid="home-minus"><Minus className="h-4 w-4" /></Button>
                  <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => adjustScore('home', 1)} data-testid="home-plus-1"><Plus className="h-3 w-3 mr-0.5" />1</Button>
                  <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => adjustScore('home', 2)} data-testid="home-plus-2"><Plus className="h-3 w-3 mr-0.5" />2</Button>
                  <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => adjustScore('home', 3)} data-testid="home-plus-3"><Plus className="h-3 w-3 mr-0.5" />3</Button>
                </div>
              )}
            </div>

            {/* VS / Period */}
            <div className="text-center">
              <p className={`text-2xl font-bold ${sub}`}>vs</p>
              {status !== 'not_started' && (
                <p className={`text-xs mt-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{periodLabel} Period</p>
              )}
            </div>

            {/* Away */}
            <div className="text-center">
              <p className={`text-sm font-medium mb-2 ${txt}`}>{selectedGame.home_away === 'away' ? 'HOME' : 'AWAY'}</p>
              <p className={`text-5xl font-bold tabular-nums ${isDark ? 'text-red-400' : 'text-red-600'}`} data-testid="away-score">{awayScore}</p>
              <p className={`text-xs mt-1 ${sub}`}>{selectedGame.opponent}</p>
              {status === 'in_progress' && (
                <div className="flex justify-center gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => adjustScore('away', -1)} className={isDark ? 'border-slate-600' : ''} data-testid="away-minus"><Minus className="h-4 w-4" /></Button>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => adjustScore('away', 1)} data-testid="away-plus-1"><Plus className="h-3 w-3 mr-0.5" />1</Button>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => adjustScore('away', 2)} data-testid="away-plus-2"><Plus className="h-3 w-3 mr-0.5" />2</Button>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => adjustScore('away', 3)} data-testid="away-plus-3"><Plus className="h-3 w-3 mr-0.5" />3</Button>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-3 mt-6">
            {status === 'not_started' && (
              <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={startGame} data-testid="start-game-btn">
                <Timer className="h-4 w-4 mr-2" />Start Game
              </Button>
            )}
            {status === 'in_progress' && (
              <>
                <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={advancePeriod} data-testid="advance-period-btn">
                  <Clock className="h-4 w-4 mr-1" />End {periodLabel}
                </Button>
                <Button variant="outline" onClick={() => saveScore()} disabled={saving} className={isDark ? 'border-slate-600 text-slate-300' : ''} data-testid="save-score-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Save</>}
                </Button>
                <Button className="bg-slate-600 hover:bg-slate-700 text-white" onClick={() => saveScore('final')} disabled={saving} data-testid="finalize-game-btn">
                  <Trophy className="h-4 w-4 mr-1" />Finalize
                </Button>
              </>
            )}
            {status === 'final' && (
              <div className={`text-center ${sub}`}>
                <Trophy className={`h-8 w-8 mx-auto mb-1 ${homeScore > awayScore ? 'text-green-500' : homeScore < awayScore ? 'text-red-500' : 'text-amber-500'}`} />
                <p className={`text-sm font-medium ${txt}`}>
                  {homeScore > awayScore ? 'Victory!' : homeScore < awayScore ? 'Defeat' : 'Draw'}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Scoring Log */}
      {scoringLog.length > 0 && (
        <Card className={bg}>
          <CardHeader className="pb-2">
            <CardTitle className={`text-sm ${txt}`}>Scoring Log</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {scoringLog.map((entry, i) => (
                <div key={i} className={`flex items-center justify-between text-xs py-1 px-2 rounded ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                  <span className={entry.team === 'home' ? 'text-cyan-500' : 'text-red-500'}>
                    {entry.team === 'home' ? 'Home' : 'Away'} +{entry.points}
                  </span>
                  <span className={sub}>{entry.period}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
